import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { FaDownload } from 'react-icons/fa';
import './HomepageCard.scss';

const REDDICKS_RESOURCE_ADDRESS = 'resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp';
const ASTROLESCENT_API_URL = 'https://api.astrolescent.com/partner/reddicks/prices';
const JUSTLOCK_API_URL = `https://api.justlock.io/resource/${REDDICKS_RESOURCE_ADDRESS}`;
const OCISWAP_API_URL = `https://api.ociswap.com/tokens/${REDDICKS_RESOURCE_ADDRESS}`;

const HomepageCard = () => {
  const [tokenData, setTokenData] = useState(null);
  const [lockData, setLockData] = useState(null);
  const [ociswapData, setOciswapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch price data
        const priceResponse = await fetch(ASTROLESCENT_API_URL);
        if (!priceResponse.ok) {
          throw new Error('Failed to fetch price data from Astrolescent');
        }
        const priceData = await priceResponse.json();
        const reddicksPriceData = priceData[REDDICKS_RESOURCE_ADDRESS];
        if (!reddicksPriceData) {
            throw new Error('REDDICKS token data not found in Astrolescent response');
        }
        setTokenData(reddicksPriceData);

        // Fetch lock data
        const lockResponse = await fetch(JUSTLOCK_API_URL);
        if (!lockResponse.ok) {
          console.warn('Failed to fetch lock data from JustLock, proceeding without it.');
          setLockData(null);
        } else {
          const lockJson = await lockResponse.json();
          setLockData(lockJson);
        }

        // Fetch data from OCISwap
        const ociswapResponse = await fetch(OCISWAP_API_URL);
        if (!ociswapResponse.ok) {
            console.warn('Failed to fetch data from OCISwap, proceeding without it.');
            setOciswapData(null);
        } else {
            const ociswapJson = await ociswapResponse.json();
            setOciswapData(ociswapJson);
        }

        setLoading(false);
        // Trigger the animation after a short delay
        setTimeout(() => setIsVisible(true), 300); 

      } catch (err) {
        console.error("Error fetching token data:", err);
        setError(err.message || 'Failed to load token data.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper to format numbers
  const formatNumber = (num, decimals = 2) => {
    if (typeof num !== 'number') return 'N/A';
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  
  // Helper to format large numbers (like market cap) into K, M, B
  const formatLargeNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return 'N/A';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toFixed(0); // Show without suffix if less than 1000
  };

  // Helper to format price with more precision for smaller values
  const formatPrice = (price) => {
    if (typeof price !== 'number') return 'N/A';
    if (price < 0.0001 && price > 0) return price.toExponential(2);
    if (price < 1) return price.toFixed(6);
    return formatNumber(price, 2);
  };

  // Helper for percentage change display
  const formatPercentage = (diff) => {
      if (typeof diff !== 'number') return { value: 'N/A', className: '' };
      const value = (diff * 100).toFixed(2);
      const className = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
      return { value: `${value}%`, className };
  };

  const totalSupply = tokenData?.totalSupply ? parseFloat(tokenData.totalSupply) : 0;
  const lockedAmount = lockData?.lock_summary?.amount ? parseFloat(lockData.lock_summary.amount) : 0;
  const lockedPercentage = totalSupply > 0 && lockedAmount > 0 ? ((lockedAmount / totalSupply) * 100).toFixed(2) : 0;

  const priceUSD = tokenData?.tokenPriceUSD;
  const priceXRD = tokenData?.tokenPriceXRD;
  const diff7DaysUSD = formatPercentage(tokenData?.diff7DaysUSD);
  const diff7DaysXRD = formatPercentage(tokenData?.diff7Days);

  // Extract Market Cap Data
  const marketCapUSDString = ociswapData?.market_cap?.circulating?.usd?.now;
  const marketCapUSD = marketCapUSDString ? parseFloat(marketCapUSDString) : null;

  const handleDownload = () => {
    if (!cardRef.current) return;

    const originalCard = cardRef.current;
    // Clone the card element
    const cardClone = originalCard.cloneNode(true);

    // Style the clone for off-screen rendering
    cardClone.style.position = 'absolute';
    cardClone.style.left = '-9999px';
    cardClone.style.top = '0px';
    cardClone.style.width = `${originalCard.offsetWidth}px`; // Maintain original width
    cardClone.style.height = `${originalCard.offsetHeight}px`; // Maintain original height
    cardClone.style.zIndex = '-100'; // Ensure it's not visible

    // --- Apply simplified styles directly to the clone --- 
    // Force a solid background color for the capture
    cardClone.style.background = 'rgba(20, 22, 40, 0.95)'; 
    // Remove potentially problematic styles
    cardClone.style.border = 'none'; // Remove border
    cardClone.style.animation = 'none'; // Disable animations
    cardClone.style.backdropFilter = 'none'; // Disable backdrop filter (if applicable)

    // Remove the download button from the clone
    const buttonClone = cardClone.querySelector('.download-button');
    if (buttonClone) {
      buttonClone.remove();
    }

    // Append the clone to the body to allow rendering
    document.body.appendChild(cardClone);

    // Ensure styles are applied before capturing
    requestAnimationFrame(() => {
      html2canvas(cardClone, { // Capture the clone, not the original
        backgroundColor: null, // Background is now set directly on the clone
        useCORS: true,
        logging: false,
        width: cardClone.offsetWidth, // Explicitly set width/height from clone
        height: cardClone.offsetHeight,
        // scale: window.devicePixelRatio || 1 // Optional: Improve resolution
      }).then(canvas => {
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'reddicks-stats.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch(err => {
        console.error('Error generating card image:', err);
      }).finally(() => {
        // Always remove the clone, even if there was an error
        document.body.removeChild(cardClone);
      });
    });
  };

  return (
    <div ref={cardRef} className={`homepage-card ${isVisible ? 'visible' : ''} ${loading ? 'loading' : ''} ${error ? 'error' : ''}`}>
      <div className="card-content">
        {loading && <div className="loading-spinner"></div>}
        {error && <div className="error-message">Error: {error}</div>}
        {!loading && !error && tokenData && lockData && (
          <>
            <div className="card-header">
              <img src={tokenData.iconUrl} alt={`${tokenData.name} logo`} className="token-icon" />
              <h3>
                <span>{tokenData.symbol}</span>
                <span>Stats</span>
              </h3>
              <div className="live-indicator"></div>
            </div>
            <div className="card-body">
              <div className="stat-row price-row">
                <div className="stat-item">
                  <span className="label">Price USD:</span>
                  <span className="value">${formatPrice(priceUSD)}</span>
                </div>
                <div className="stat-item">
                    <span className="label">7d:</span>
                    <span className={`value ${diff7DaysUSD.className}`}>{diff7DaysUSD.value}</span>
                </div>
              </div>
              <div className="stat-row price-row">
                <div className="stat-item">
                  <span className="label">Price XRD:</span>
                  <span className="value">{formatPrice(priceXRD)}</span>
                </div>
                 <div className="stat-item">
                    <span className="label">7d:</span>
                    <span className={`value ${diff7DaysXRD.className}`}>{diff7DaysXRD.value}</span>
                </div>
              </div>
              <hr className="divider" />
              <div className="stat-row">
                <div className="stat-item">
                    <span className="label">Market Cap USD:</span>
                    <span className="value">${formatLargeNumber(marketCapUSD)}</span>
                </div>
              </div>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="label">Total Supply:</span>
                  <span className="value">{formatNumber(totalSupply, 0)}</span>
                </div>
              </div>
               <div className="stat-row">
                <div className="stat-item">
                  <span className="label">Total Locked:</span>
                  <span className="value">
                    {lockData ? `${formatNumber(lockedAmount, 0)} (${lockedPercentage}%)` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div className="card-footer">
                <a href="https://www.caviarnine.com/charts?left=resource_rdx1tknxxxxxxxxxradxrdxxxxxxxxx009923554798xxxxxxxxxradxrd&right=resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp" target="_blank" rel="noopener noreferrer" className="trade-link">
                    Trade Reddicks
                </a>
                <button onClick={handleDownload} className="download-button" aria-label="Download Card Image">
                  <FaDownload />
                </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HomepageCard; 